import React, {useMemo} from 'react';
import DashboardLayout from "../../../layouts/DashboardLayout";
import PageHeader from "../../../components/PageHeader";
import {Link, useParams} from 'react-router-dom';
import {useMutation, useQuery} from "react-query";
import StatusBadge from "../../../components/StatusBadge";
import ButtonLink from "../../../components/ButtonLink";
import Loading from "../../../components/Loading";
import {CANCEL_PAYMENT, GET_PAYMENT_TRANSACTIONS} from "../../../utils/graphql";
import {apollo} from "../../../utils/apollo";
import InformationLine from "../../../components/InformationLine";
import ProductsDetails from "../../../components/ProductsDetails";
import MoneyHighlight from "../../../components/MoneyHighlight";
import {FlagIcon, CreditCardIcon, ClockIcon, DocumentTextIcon, UserIcon} from "@heroicons/react/24/solid";
import Button from "../../../components/Button";
import InvoiceField from "../../../components/InvoiceField";
import TransactionList from "../../../TransactionList";
import {useFetchPaymentQuery} from "../../../utils/queries";
import ButtonInternalLink from "../../../components/ButtonInternalLink";
import useModalStore from "../../../store/modal";

function PaymentView() {
    const {payment_uuid} = useParams();
    const { setModal } = useModalStore();

    const fetchPaymentQuery = useFetchPaymentQuery(payment_uuid, {refetchInterval: 10000});

    const fetchPaymentTransactions = useQuery({
        queryKey: 'payment_transactions_view', queryFn: async () => {
            const {data} = await apollo.query({
                query: GET_PAYMENT_TRANSACTIONS, variables: {
                    paymentUuid: payment_uuid,
                },
            });
            return data?.getPaymentTransactions;
        }, keepPreviousData: false
    });

    const cancelPaymentMutation = useMutation({
        mutationKey: 'cancel_payment', mutationFn: async () => {
            const {data} = await apollo.mutate({
                mutation: CANCEL_PAYMENT, variables: {
                    paymentUuid: payment_uuid
                },
            });
            return data?.cancelPayment;
        }, onSuccess: (canceled) => {
            if (canceled === true) {
                fetchPaymentQuery.refetch();
            }
        }
    })

    const payment = useMemo(() => fetchPaymentQuery?.isSuccess ? fetchPaymentQuery?.data : null, [fetchPaymentQuery]);
    const transactions = useMemo(() => fetchPaymentTransactions?.isSuccess ? fetchPaymentTransactions?.data : null, [fetchPaymentTransactions]);

    return (<DashboardLayout>
        <PageHeader back="/merchant/payments" title="Payment Details"
                    description="View and manage payment information, customer details, and transaction history."/>

        {(fetchPaymentQuery.isLoading) && <Loading/>}
        {(fetchPaymentQuery.isSuccess && payment) && <div className="mt-6">
            {/* Payment Status Banner */}
            <div className="mb-6 space-y-4">
                {/* Payment Status Header */}
                <div className={`p-5 rounded-t-xl shadow-sm border-b ${
                    payment?.status === 'completed' ? 'bg-green-50 border-green-200' : 
                    payment?.status === 'pending' ? 'bg-yellow-50 border-yellow-200' : 
                    'bg-red-50 border-red-200'
                }`}>
                    <div className="flex items-center gap-4">
                        <div className={`p-3 rounded-full ${
                            payment?.status === 'completed' ? 'bg-green-200' : 
                            payment?.status === 'pending' ? 'bg-yellow-200' : 
                            'bg-red-200'
                        }`}>
                            <CreditCardIcon className={`h-7 w-7 ${
                                payment?.status === 'completed' ? 'text-green-700' : 
                                payment?.status === 'pending' ? 'text-yellow-700' : 
                                'text-red-700'
                            }`} />
                        </div>
                        <div>
                            <h2 className="text-xl font-semibold text-gray-900">Payment {payment?.status.charAt(0).toUpperCase() + payment?.status.slice(1)}</h2>
                            <p className="text-sm text-gray-700">ID: {payment?.paymentUuid}</p>
                        </div>
                    </div>
                </div>

                {/* Payment Status Details */}
                <div className={`p-5 rounded-b-xl shadow-md border-t ${
                    payment?.status === 'completed' ? 'bg-white border-green-200' : 
                    payment?.status === 'pending' ? 'bg-white border-yellow-200' : 
                    'bg-white border-red-200'
                }`}>
                    <div className="flex flex-wrap items-center gap-6 justify-between">
                        <div className="flex items-center gap-4">
                            <div className="font-medium text-gray-700">Amount:</div>
                            <MoneyHighlight value={payment?.amount} currency={payment?.currency?.symbol} />
                        </div>

                        <div className="flex flex-wrap items-center gap-4">
                            <div className="flex items-center gap-2">
                                <div className="font-medium text-gray-700">Status:</div>
                                <StatusBadge status={payment?.status} />
                            </div>

                            <div className="flex items-center gap-2">
                                <div className="font-medium text-gray-700">Mode:</div>
                                {payment?.test ? 
                                    <span className="bg-red-100 text-red-700 font-bold text-sm px-3 py-1.5 border border-red-300 rounded-full shadow-sm">Test</span> : 
                                    <span className="bg-green-100 text-green-700 font-bold text-sm px-3 py-1.5 border border-green-300 rounded-full shadow-sm">Live</span>
                                }
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Left Column - Payment Details */}
                <div className="lg:col-span-2 space-y-6">
                    {/* Payment Information Card */}
                    <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                        <div className="px-6 py-4 bg-gradient-to-r from-blue-50 to-indigo-50 border-b border-gray-200 flex items-center gap-2">
                            <DocumentTextIcon className="h-5 w-5 text-blue-600" />
                            <h3 className="text-lg font-medium text-gray-800">Payment Information</h3>
                        </div>
                        <div className="p-4">
                            <dl className="divide-y divide-gray-100">
                                <InformationLine title="Payment ID" value={payment?.paymentUuid}/>
                                <InformationLine title="Supplier" value={payment?.supplier?.supplierName}/>
                                <InformationLine title="Gateway Reference" value={payment?.gatewayReferenceNumber}/>
                                <InformationLine title="Customer Reference" value={payment?.customerReferenceNumber}/>
                                <InformationLine title="Broker Reference" value={payment?.brokerReference}/>
                                <InformationLine title="Auth Code" value={payment?.authCode}/>
                                <InformationLine title="Card Number" value={payment?.cardNumber}/>
                                <InformationLine title="Anti Fraud" value={payment?.antiFraud && <FlagIcon className="h-5 w-5 text-rose-500"/>}/>
                                <InformationLine title="Created At" value={payment?.createdAt}/>
                                <InformationLine title="Paid At" value={payment?.paidAt}/>
                                <InformationLine title="Refunded At" value={payment?.refundedAt}/>
                                <InformationLine title="Expires At" value={payment?.expiresAt}/>
                                <InformationLine title="Settlement ID" value={payment?.settlement?.settlementUuid}/>
                                <InformationLine title="Card Holder Name" value={payment?.cardHolderName}/>
                                <InformationLine title="IP Address" value={payment?.ipAddress}/>
                                <InformationLine title="International" value={payment?.isInternational ? "Yes" : "No"}/>
                                <InformationLine title="Split Payment" value={payment?.splitPayment ? "Yes" : "No"}/>
                                {/*<InformationLine title="Gateway Code" value={payment?.gatewayCode}/>*/}
                                <InformationLine title="Callback URL" value={payment?.callbackUrl}/>
                                <InformationLine title="Remarks" value={payment?.remarks}/>
                            </dl>
                        </div>
                    </div>

                    {/* Products Details Card */}
                    <ProductsDetails payment={payment}/>

                    {/* Transaction History */}
                    {fetchPaymentTransactions.isLoading && <Loading/>}
                    {fetchPaymentTransactions.isSuccess && !!transactions?.length &&
                        <TransactionList key={payment?.paymentUuid} transactions={transactions}/>}
                </div>

                {/* Right Column - Customer Details & Actions */}
                <div className="space-y-6">
                    {/* Customer Details Card */}
                    <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                        <div className="px-6 py-4 bg-gradient-to-r from-blue-50 to-indigo-50 border-b border-gray-200 flex items-center gap-2">
                            <UserIcon className="h-5 w-5 text-blue-600" />
                            <h3 className="text-lg font-medium text-gray-800">Customer Details</h3>
                        </div>
                        <div className="p-4">
                            <div className="space-y-3">
                                <InvoiceField name="Name" value={payment?.name}
                                    className="col-span-12 mt-1 text-sm leading-6 text-gray-700 sm:mt-0 p-3 bg-white border border-gray-200 shadow-sm rounded-lg"/>
                                <InvoiceField name="Email" value={payment?.email}
                                    className="col-span-12 mt-1 text-sm leading-6 text-gray-700 sm:mt-0 p-3 bg-white border border-gray-200 shadow-sm rounded-lg"/>
                                <InvoiceField name="Phone" value={payment?.phone}
                                    className="col-span-12 mt-1 text-sm leading-6 text-gray-700 sm:mt-0 p-3 bg-white border border-gray-200 shadow-sm rounded-lg"/>
                                <InvoiceField name="Address" value={payment?.address}
                                    className="col-span-12 mt-1 text-sm leading-6 text-gray-700 sm:mt-0 p-3 bg-white border border-gray-200 shadow-sm rounded-lg"/>
                                <div className="grid grid-cols-2 gap-3">
                                    <InvoiceField name="City" value={payment?.city}
                                        className="col-span-1 mt-1 text-sm leading-6 text-gray-700 sm:mt-0 p-3 bg-white border border-gray-200 shadow-sm rounded-lg"/>
                                    <InvoiceField name="Zip Code" value={payment?.zipCode}
                                        className="col-span-1 mt-1 text-sm leading-6 text-gray-700 sm:mt-0 p-3 bg-white border border-gray-200 shadow-sm rounded-lg"/>
                                </div>
                                <InvoiceField name="Country" value={payment?.country}
                                    className="col-span-12 mt-1 text-sm leading-6 text-gray-700 sm:mt-0 p-3 bg-white border border-gray-200 shadow-sm rounded-lg"/>
                            </div>
                        </div>
                    </div>

                    {/* Actions Card */}
                    <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                        <div className="px-6 py-4 bg-gradient-to-r from-blue-50 to-indigo-50 border-b border-gray-200 flex items-center gap-2">
                            <ClockIcon className="h-5 w-5 text-blue-600" />
                            <h3 className="text-lg font-medium text-gray-800">Payment Actions</h3>
                        </div>
                        <div className="p-4">
                            <div className="flex flex-col gap-3">
                                {['pending', 'failed'].includes(payment?.status) &&
                                    <ButtonLink target="_blank" href={`/pay/${payment?.paymentUuid}`} className="w-full justify-center">
                                        Payment Link
                                    </ButtonLink>
                                }

                                {['pending', 'failed'].includes(payment?.status) &&
                                    <Button 
                                        secondary={true} 
                                        onClick={() => {
                                            setModal({
                                                isVisible: true,
                                                title: "Cancel Payment",
                                                description: "Are you sure you want to cancel this payment? This action cannot be undone.",
                                                confirmText: "Yes, Cancel Payment",
                                                onCancel: () => {

                                                },
                                                onConfirm: () => {
                                                    cancelPaymentMutation.mutate();
                                                }
                                            });
                                        }}
                                        disabled={cancelPaymentMutation.isLoading} 
                                        className="w-full justify-center"
                                    >
                                        Cancel Payment
                                    </Button>
                                }

                                {['completed'].includes(payment?.status) && payment?.isRefundAutomated && !payment?.refunds?.length &&
                                    <ButtonLink secondary={true} href={`/merchant/refunds/request/${payment?.paymentUuid}`} className="w-full justify-center">
                                        Refund Payment
                                    </ButtonLink>
                                }

                                {['completed'].includes(payment?.status) && payment?.callbackUrlAfterTransaction &&
                                    <ButtonLink href={payment?.callbackUrlAfterTransaction} target="_blank" className="w-full justify-center">
                                        Go to Callback URL
                                    </ButtonLink>
                                }

                                {!!payment?.refunds?.length &&
                                    <div className="mt-2">
                                        <div className="flex items-center gap-2 mb-2">
                                            <FlagIcon className="h-5 w-5 text-yellow-600"/>
                                            <span className="text-yellow-600 font-medium">Refund Information</span>
                                        </div>
                                        <div className="flex flex-col gap-2">
                                            {payment?.refunds?.map((refund, index) => (
                                                <Link key={index} to={`/merchant/refunds/view/${refund?.refundUuid}`}
                                                    className="bg-white border border-gray-200 text-gray-700 p-3 rounded-lg shadow-sm text-sm hover:bg-gray-50 transition-colors flex justify-between items-center">
                                                    <span>Refund ID: {refund?.refundUuid}</span>
                                                    <span className="text-blue-600">View →</span>
                                                </Link>
                                            ))}
                                        </div>
                                    </div>
                                }
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>}

    </DashboardLayout>);
}

export default PaymentView;
